import React, { forwardRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Button from '@starbucks-web/pattern-library/lib/components/button';
import ContentColumn from '@starbucks-web/pattern-library/lib/components/content-column';
import ContentCrateLoading from 'shared/app/components/content-crate-loading';
import SharedForm from 'shared/app/components/shared-form';
import useErrorNotification from 'shared/app/components/error-notification/use-error-notification';

import { signedInSelector } from 'shared/app/bundles/user';

import ClaimList from '../claim-list';
import messages from './messages';
import { sharedCallsToAction } from 'shared/app/messages/ctas';

import {
  oauthAuthorizeStatusSelector,
  cancelUriSelector,
} from '../../state/selectors';
import {
  checkOauthAuthorizeStatus,
  confirmOauthAuthorize,
} from '../../state/action-creators';

const AuthorizeForm = forwardRef((_, ref) => {
  const dispatch = useDispatch();
  const { showErrorDialog } = useErrorNotification();

  const isSignedIn = useSelector(signedInSelector);

  useEffect(() => {
    if (isSignedIn) {
      const checkStatus = async () => {
        try {
          const authorizationDetails = await dispatch(
            checkOauthAuthorizeStatus()
          );
          if (
            authorizationDetails &&
            authorizationDetails.authorized === true &&
            authorizationDetails.redirectUri
          ) {
            window.location.href = authorizationDetails.redirectUri;
          }
        } catch {
          return null;
        }
      };
      checkStatus();
    }
  }, [isSignedIn]);

  const oauthAuthorizeStatus = useSelector(oauthAuthorizeStatusSelector);
  const cancelUri = useSelector(cancelUriSelector);
  const { data, loading } = oauthAuthorizeStatus || {};

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    try {
      const redirectUri = await dispatch(confirmOauthAuthorize());
      window.location.href = redirectUri;
    } catch {
      showErrorDialog();
    }
  };

  if (!loading && data?.authorized === true && data?.redirectUri) {
    return null;
  }

  return (
    <ContentColumn className="mt4 md-mt8" size="sm">
      {loading ? (
        <ContentCrateLoading />
      ) : (
        <SharedForm onSubmit={handleSubmit} ref={ref}>
          {data?.clientImage ? (
            <img alt="" className="mb2 " src={data?.clientImage} />
          ) : null}
          <p className="text-sm">
            <FormattedMessage
              {...messages.accessDescription}
              values={{
                clientName: <b>{data?.clientName}</b>,
              }}
            />
          </p>
          <ClaimList />
          <div className="py6 text-right">
            <Button className="mr3" href={cancelUri}>
              <FormattedMessage {...sharedCallsToAction.cancel} />
            </Button>
            <Button
              data-e2e="confirmButton"
              type="submit"
              visualStyle="positive"
            >
              <FormattedMessage {...messages.confirmButton} />
            </Button>
          </div>
        </SharedForm>
      )}
    </ContentColumn>
  );
});

export default AuthorizeForm;
